/**
 * Utilitaires pour décoder et vérifier les JWT
 */

interface JWTPayload {
  id: string;
  email: string;
  role: string;
  iat?: number;
  exp?: number;
  type?: string;
}

/**
 * Décoder un JWT sans vérifier la signature (utilisation côté client)
 */
export function decodeToken(token: string): JWTPayload | null {
  try {
    const parts = token.split(".");
    if (parts.length !== 3) {
      return null;
    }

    // Décoder la partie payload (deuxième partie)
    const payload = JSON.parse(atob(parts[1]));
    return payload as JWTPayload;
  } catch (error) {
    console.error("Erreur lors du décodage du JWT:", error);
    return null;
  }
}

/**
 * Vérifier si un token est expiré
 */
export function isTokenExpired(token: string): boolean {
  const decoded = decodeToken(token);
  if (!decoded || !decoded.exp) {
    return true;
  }

  // exp est en secondes, Date.now() en millisecondes
  const expirationTime = decoded.exp * 1000;
  return Date.now() >= expirationTime;
}

/**
 * Obtenir le temps avant expiration en millisecondes
 * Retourne null si le token est invalide ou expiré
 */
export function getTokenExpirationTime(token: string): number | null {
  const decoded = decodeToken(token);
  if (!decoded || !decoded.exp) {
    return null;
  }

  const expirationTime = decoded.exp * 1000;
  const timeRemaining = expirationTime - Date.now();

  // Retourner le temps restant ou 0 s'il est déjà expiré
  return timeRemaining > 0 ? timeRemaining : 0;
}

/**
 * Vérifier si le token va expirer bientôt (dans les 5 minutes)
 */
export function isTokenExpiringsoon(
  token: string,
  thresholdMinutes: number = 5,
): boolean {
  const timeRemaining = getTokenExpirationTime(token);
  if (timeRemaining === null) {
    return true; // Token invalide, considérer comme expirant bientôt
  }

  const thresholdMs = thresholdMinutes * 60 * 1000;
  return timeRemaining < thresholdMs;
}

/**
 * Extraire les informations utilisateur du token
 */
export function getUserFromToken(
  token: string,
): Omit<JWTPayload, "iat" | "exp" | "type"> | null {
  const decoded = decodeToken(token);
  if (!decoded) {
    return null;
  }

  return {
    id: decoded.id,
    email: decoded.email,
    role: decoded.role,
  };
}
